import React, { useState, useEffect } from "react";

function formatUSD(value) {
  if (!value) return "—";
  const v = Number(value);
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(0)}`;
}

export default function TopLosers({ data }) {
  /* ---------------- ROW LIMITS (persistent) ---------------- */
  const [rows5m, setRows5m] = useState(Number(localStorage.getItem("losersRows5m")) || 10);
  const [rows15m, setRows15m] = useState(Number(localStorage.getItem("losersRows15m")) || 10);
  const [rows1h, setRows1h] = useState(Number(localStorage.getItem("losersRows1h")) || 10);
  const [rows24h, setRows24h] = useState(Number(localStorage.getItem("losersRows24h")) || 10);

  useEffect(() => { localStorage.setItem("losersRows5m", rows5m); }, [rows5m]);
  useEffect(() => { localStorage.setItem("losersRows15m", rows15m); }, [rows15m]);
  useEffect(() => { localStorage.setItem("losersRows1h", rows1h); }, [rows1h]);
  useEffect(() => { localStorage.setItem("losersRows24h", rows24h); }, [rows24h]);

  /* ---------------- EXCHANGE FILTER ---------------- */
  const [selectedExchanges, setSelectedExchanges] = useState(["MEXC", "HyperLiquid", "Bitunix"]);
  const toggleExchange = (ex) => {
    setSelectedExchanges((prev) =>
      prev.includes(ex) ? prev.filter((e) => e !== ex) : [...prev, ex]
    );
  };

  /* ---------------- FILTER DATA BY SELECTED EXCHANGES ---------------- */
  const filterByExchange = (rows = []) =>
    rows.filter((c) => c.exchanges?.some((e) => selectedExchanges.includes(e)));

  const filtered5m = filterByExchange(data.losers5m);
  const filtered15m = filterByExchange(data.losers15m);
  const filtered1h = filterByExchange(data.losers1h);
  const filtered24h = filterByExchange(data.losers24h);

  return (
    <div className="page-section">
      {/* Page Header */}
      <header className="page-header">
        <h2>Top Losers</h2>
        <p className="page-subtitle">Track the biggest losers across multiple timeframes</p>
      </header>

      {/* Tables with exchange toggles */}
      <LoserSection
        title="Top Losers — 5 Minutes"
        rows={filtered5m}
        limit={rows5m}
        onLimitChange={setRows5m}
      >
        <div className="exchange-toggles" style={{ marginBottom: "10px" }}>
          {["MEXC", "HyperLiquid", "Bitunix"].map((ex) => (
            <label key={ex} className="switch">
              <input
                type="checkbox"
                checked={selectedExchanges.includes(ex)}
                onChange={() => toggleExchange(ex)}
              />
              <span className="slider"></span>
              <span className="switch-label">{ex}</span>
            </label>
          ))}
        </div>
      </LoserSection>

      <LoserSection
        title="Top Losers — 15 Minutes"
        rows={filtered15m}
        limit={rows15m}
        onLimitChange={setRows15m}
      />
      <LoserSection
        title="Top Losers — 1 Hour"
        rows={filtered1h}
        limit={rows1h}
        onLimitChange={setRows1h}
      />
      <LoserSection
        title="Top Losers — 24 Hours"
        rows={filtered24h}
        limit={rows24h}
        onLimitChange={setRows24h}
      />
    </div>
  );
}

/* ---------------------------------------
   Table Section
--------------------------------------- */
function LoserSection({ title, rows = [], limit, onLimitChange, children }) {
  const visibleRows = rows.slice(0, limit);

  return (
    <section className="section">
      {children}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <h2 style={{ marginBottom: 0 }}>{title}</h2>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          style={{
            background: "var(--bg-card)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            padding: "4px 8px",
            fontSize: "13px",
            marginTop: "25px",
            cursor: "pointer"
          }}
        >
          <option value={5}>5 rows</option>
          <option value={10}>10 rows</option>
          <option value={15}>15 rows</option>
          <option value={25}>25 rows</option>
        </select>
      </div>

      {visibleRows.length === 0 ? (
        <p className="loading">
          {title.includes("24 Hours") ? "⏳ Gathering 24h data — check back soon" : "No losers yet"}
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Price</th>
              <th>Change</th>
              <th>Market Cap</th>
              <th>24h Volume</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((c) => {
              const volume = Number(c.volume24h ?? 0);
              const marketCap = Number(c.marketCap ?? 0);
              const isVolumeSpike = marketCap > 0 && volume > marketCap;

              return (
                <tr
                  key={`${c.coin}-${c.symbol}`}
                  className={isVolumeSpike ? "volume-spike" : ""}
                >
                  <td>{c.symbol}</td>
                  <td>${Number(c.price).toFixed(6)}</td>
                  <td className={c.change >= 0 ? "change up" : "change down"}>
                    {c.change >= 0 ? "+" : ""}
                    {c.change.toFixed(2)}%
                  </td>
                  <td>{formatUSD(c.marketCap)}</td>
                  <td>{formatUSD(c.volume24h)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
